import { SalesComparisonApproachType, ValueType } from '@aloft/toolkit-shared';

import { AdditionalFeatureKey, DetailField, SalesComparisonColumns } from './types.ts';

const siteRowsDepreciatedCost: SalesComparisonColumns[0][] = [
  {
    key: ['siteSize', 'siteInfluence', 'view'],
    label: ['Site Size', 'Site Influence', 'View'],
    adjustment: 'siteSizeAdjustment',
  },
];

const siteRowsUnitRate: SalesComparisonColumns[0][] = [
  { key: ['siteSize'], label: ['Site Size'], adjustment: 'siteSizeAdjustment' },
  { key: ['siteInfluence'], label: ['Site Influence'], adjustment: 'siteInfluenceAdjustment' },
  { key: ['view'], label: ['View'], adjustment: 'siteViewAdjustment' },
];

const bathRowsDepreciatedCost: SalesComparisonColumns[0][] = [
  { key: ['bathAboveGrade'], label: ['Baths'], adjustment: 'bathAboveGradeAdjustment' },
];

const bathRowsUnitRate: SalesComparisonColumns[0][] = [
  { key: ['fullBaths'], label: ['Full Baths'], adjustment: 'fullBathsAdjustment' },
  { key: ['halfBaths'], label: ['Half Baths'], adjustment: 'halfBathsAdjustment' },
];

const basementBathRowsDepreciatedCost: SalesComparisonColumns[0][] = [
  {
    key: ['basementBaths'],
    label: ['Basement Baths'],
    adjustment: 'basementBathsAdjustment',
  },
];

const basementBathRowsUnitRate: SalesComparisonColumns[0][] = [
  {
    key: ['additionalFeatures.basementFullBaths'],
    label: ['Basement Full Baths'],
    adjustment: 'basementFullBathsAdjustment',
  },
  {
    key: ['additionalFeatures.basementHalfBaths'],
    label: ['Basement Half Baths'],
    adjustment: 'basementHalfBathsAdjustment',
  },
];

const basementAreaRowsDepreciatedCost: SalesComparisonColumns[0][] = [
  {
    key: ['additionalFeatures.basementArea'],
    label: ['Basement'],
    adjustment: 'basementAreaAdjustment',
  },
];

const basementAreaRowsUnitRate: SalesComparisonColumns[0][] = [
  {
    key: ['additionalFeatures.basementTotal'],
    label: ['Basement Total'],
    adjustment: 'basementTotalAdjustment',
  },
  {
    key: ['additionalFeatures.basementFinished'],
    label: ['Basement Finished'],
    adjustment: 'basementFinishedAdjustment',
  },
];

export const getSalesComparisonColumns = (
  salesComparisonApproach?: SalesComparisonApproachType,
): SalesComparisonColumns => {
  const siteRows =
    salesComparisonApproach === 'unit_rate' ? siteRowsUnitRate : siteRowsDepreciatedCost;
  const bathRows =
    salesComparisonApproach === 'unit_rate' ? bathRowsUnitRate : bathRowsDepreciatedCost;
  const basementBathRows =
    salesComparisonApproach === 'unit_rate'
      ? basementBathRowsUnitRate
      : basementBathRowsDepreciatedCost;
  const basementAreaRows =
    salesComparisonApproach === 'unit_rate'
      ? basementAreaRowsUnitRate
      : basementAreaRowsDepreciatedCost;

  return [
    { key: ['dataSource'], label: ['Data Source'] },
    { key: ['propertyType'], label: ['Property Type'] },
    { key: ['proximity'], label: ['Proximity'] },
    { key: ['actualAge'], label: ['Actual Age'] },
    { key: ['listingStatus'], label: ['Listing Status'] },
    { key: ['salesPrice'], label: ['Sales Price'] },
    { key: ['listingPrice'], label: ['Listing Price'] },
    { key: ['dom'], label: ['CDOM'] },
    {
      key: ['contractDate', 'saleDate'],
      label: ['Contract Date', 'Sale Date'],
      adjustment: 'saleDateAdjustment',
    },
    ...siteRows,
    { key: ['quality'], label: ['Quality'], adjustment: 'qualityAdjustment' },
    { key: ['condition'], label: ['Condition'], adjustment: 'conditionAdjustment' },
    { key: ['gla'], label: ['GLA'], adjustment: 'glaAdjustment' },
    { key: ['bedCount'], label: ['Bed Count'], adjustment: 'bedCountAdjustment' },
    ...bathRows,
    ...basementAreaRows,
    ...basementBathRows,
    {
      key: ['additionalFeatures.garage'],
      label: ['Garage / Carport'],
      adjustment: 'garageAdjustment',
    },
    {
      key: ['additionalFeatures.porch'],
      label: ['Porch / Patio / Deck'],
      adjustment: 'porchAdjustment',
    },
    { key: ['additionalFeatures.attic'], label: ['Attic'], adjustment: 'atticAdjustment' },
    {
      key: ['additionalFeatures.fireplace'],
      label: ['Fireplace'],
      adjustment: 'fireplaceAdjustment',
    },
    { key: ['additionalFeatures.shed'], label: ['Shed'], adjustment: 'shedAdjustment' },
    { key: ['additionalFeatures.barn'], label: ['Barn'], adjustment: 'barnAdjustment' },
    { key: ['additionalFeatures.adu'], label: ['ADU'], adjustment: 'aduAdjustment' },
    { key: ['additionalFeatures.pool'], label: ['Pool / Spa'], adjustment: 'poolAdjustment' },
    { key: ['additionalFeatures.workshop'], label: ['Workshop'], adjustment: 'workshopAdjustment' },
    {
      key: ['totalCostToCureDisplayName'],
      label: ['Total Cost to Cure'],
      adjustment: 'totalCostToCureAdjustment',
    },
    { key: ['otherAdjustmentsDisplayName'], label: ['Other'], adjustment: 'otherAdjustments' },
    {
      key: ['netAdj', 'grossAdj'],
      label: ['Net Adj.', 'Gross Adj.'],
      adjustment: 'netAdjPrice',
      borderTop: true,
    },
    {
      key: ['adjSalesPrice'],
      label: ['Adj. Sales Price'],
      bold: true,
      borderTop: true,
      align: 'right',
    },
  ];
};

export const salesComparisonColumns: SalesComparisonColumns =
  getSalesComparisonColumns('depreciated_cost');

export const ADDITIONAL_FEATURE_KEYS: AdditionalFeatureKey[] = [
  'additionalFeatures.basementTotal',
  'additionalFeatures.basementFinished',
  'additionalFeatures.basementArea',
  'additionalFeatures.basementFullBaths',
  'additionalFeatures.basementHalfBaths',
  'additionalFeatures.garage',
  'additionalFeatures.porch',
  'additionalFeatures.attic',
  'additionalFeatures.fireplace',
  'additionalFeatures.pool',
  'additionalFeatures.shed',
  'additionalFeatures.barn',
  'additionalFeatures.adu',
  'additionalFeatures.workshop',
];

export const ADDITIONAL_FEATURE_LABELS: Record<AdditionalFeatureKey, string> = {
  'additionalFeatures.basementTotal': 'Basement Total',
  'additionalFeatures.basementFinished': 'Basement Finished',
  'additionalFeatures.basementArea': 'Basement',
  'additionalFeatures.basementFullBaths': 'Basement Full Baths',
  'additionalFeatures.basementHalfBaths': 'Basement Half Baths',
  'additionalFeatures.garage': 'Garage / Carport',
  'additionalFeatures.porch': 'Porch / Patio / Deck',
  'additionalFeatures.attic': 'Attic',
  'additionalFeatures.fireplace': 'Fireplace',
  'additionalFeatures.pool': 'Pool / Spa',
  'additionalFeatures.shed': 'Shed',
  'additionalFeatures.barn': 'Barn',
  'additionalFeatures.adu': 'ADU',
  'additionalFeatures.workshop': 'Workshop',
};

export const propertyDetailFields = (
  address?: string,
  propertyType?: string,
  locationType?: string,
  zoning?: string,
  zoningCompliance?: string,
  parcelNumber?: string,
  newConstruction?: boolean,
  propertyInterest?: string,
  hoa?: string,
  hoaFees?: string,
  pud?: string,
  legalDescription?: string,
): DetailField[] => [
  { key: 'address', label: 'Subject Property Address', getValue: () => address },
  { key: 'propertyType', label: 'Property Type', getValue: () => propertyType },
  { key: 'locationType', label: 'Location Type', getValue: () => locationType },
  { key: 'zoning', label: 'Zoning', getValue: () => zoning },
  { key: 'zoningCompliance', label: 'Zoning Compliance', getValue: () => zoningCompliance },
  {
    key: 'parcelNumber',
    label: 'Parcel Number',
    getValue: () => (newConstruction ? 'New Construction' : parcelNumber),
  },
  {
    key: 'propertyInterest',
    label: 'Property Interest',
    getValue: () => propertyInterest,
  },
  { key: 'hoa', label: 'Neighborhood Name', getValue: () => hoa },
  { key: 'hoaFees', label: 'Mandatory Fees', getValue: () => hoaFees },
  { key: 'pud', label: 'PUD', getValue: () => pud },
  { key: 'legalDescription', label: 'Legal Description', getValue: () => legalDescription },
];

export const orderDetailFields = (
  clientName?: string,
  clientAddress?: string,
  borrowerName?: string,
  appraisalType?: string,
  orderNumber?: string,
  inspectionDate?: string,
  effectiveDate?: string,
  intendedUsers?: string,
  intendedUse?: string,
): DetailField[] => [
  { key: 'clientName', label: 'Client Name', getValue: () => clientName },
  { key: 'clientAddress', label: 'Client Address', getValue: () => clientAddress },
  { key: 'borrowerName', label: 'Borrower Name', getValue: () => borrowerName },
  { key: 'appraisalType', label: 'Product Type', getValue: () => appraisalType },
  { key: 'orderNumber', label: 'Order Number', getValue: () => orderNumber },
  { key: 'inspectionDate', label: 'Inspection Date', getValue: () => inspectionDate },
  { key: 'effectiveDate', label: 'Effective Date', getValue: () => effectiveDate },
  { key: 'intendedUsers', label: 'Intended User(s)', getValue: () => intendedUsers },
  { key: 'intendedUse', label: 'Intended Use', getValue: () => intendedUse },
];

export const valueTypeLabels: Record<ValueType, string> = {
  'as-is': 'As-Is',
  'subject-to': 'Subject-To',
};
